import { graphql } from 'app/generated/gql'

import { gqlQuery } from 'app/lib/graphqlClient'
import { optOutKeyFor, phoneKey } from 'app/lib/smsOptOut'
import { logError } from 'lib/logging'

/**
 * Unread counts and the conversation list disagreed about opted-out threads.
 * `partitionByOptOut` (useInboxController) pulls a thread holding an OptOut
 * record out of Active, Unread and AI Agent for good, but the unread counts
 * behind the sidebar badge and the location picker counted it anyway. A STOP
 * text is itself an unread inbound message, so the two rules collide on every
 * opt-out: the badge showed work no filter but "Opted out" would show.
 *
 * These helpers give the counts the same opt-out view the list already has.
 */

/** Inbound message shape the unread counts work from: `from` is the customer. */
export type UnreadCountableMessage = {
  threadId?: string | null
  locationId?: string | null
  from?: string | null
}

/**
 * OptOut records carry a locationId, so suppression is per location: the same
 * number can be opted out of one practice and still be an open conversation at
 * another. Keys are digits-only last 10, matching `phoneKey` and the records.
 */
export function optOutSuppressionKey(locationId: string, phone: string | null | undefined): string {
  return `${locationId}:${phoneKey(phone)}`
}

/**
 * Shares `optOutKeyFor` with `optOutFor`, which is what keeps the counts and the
 * conversation list applying the same opt-out rule to the same thread.
 */
export function isUnreadSuppressed(message: UnreadCountableMessage, suppression: ReadonlySet<string>): boolean {
  if (!message.locationId) return false
  const key = optOutKeyFor(message.from)
  return key ? suppression.has(optOutSuppressionKey(message.locationId, key)) : false
}

// One clause per key, so 100 keys stays well under Lucene's 1024 max_clause_count.
const OPT_OUT_KEYS_PER_QUERY = 100

/**
 * Searches for the opt-out records behind a set of phone keys, chunked. Keys are
 * digits-only by construction (`phoneKey`), so there is nothing to escape — do
 * not run them through `sanitizeValue`, which eats digits. Returns [] for an
 * empty key list so callers can skip the request entirely.
 */
export function buildOptOutKeySearches(keys: string[]): string[] {
  const unique = [...new Set(keys.filter((key) => /^\d{10}$/.test(key)))]
  const searches: string[] = []
  for (let index = 0; index < unique.length; index += OPT_OUT_KEYS_PER_QUERY) {
    const chunk = unique.slice(index, index + OPT_OUT_KEYS_PER_QUERY)
    searches.push(`type:phone AND key:(${chunk.map((key) => `"${key}"`).join(` OR `)})`)
  }
  return searches
}

const OPT_OUT_KEYS_QUERY = graphql(`
  query UnreadOptOutKeys($search: String, $pagination: PaginationInput) {
    optOuts(search: $search, pagination: $pagination) {
      hits {
        key
        locationId
      }
    }
  }
`)

type OptOutKeysResult = { optOuts?: { hits?: Array<{ key?: string | null; locationId?: string | null }> } }

/**
 * `location:key` for every opt-out covering one of these unread messages.
 *
 * Targeted at the senders actually holding unread messages rather than pulling
 * the location's whole opt-out list: unread is normally a handful of threads, so
 * this is one small query on the poll, and none at all when there is no unread.
 *
 * A failed fetch under-suppresses. That direction is deliberate: a count that is
 * too high is a visible annoyance, while one that is too low hides real messages.
 * `allSettled` rather than `all` so a single failed chunk costs only its own keys
 * instead of discarding the chunks that did come back.
 */
export async function fetchUnreadOptOutSuppression(
  messages: ReadonlyArray<UnreadCountableMessage>,
): Promise<ReadonlySet<string>> {
  const searches = buildOptOutKeySearches(messages.map((message) => optOutKeyFor(message.from) ?? ``))
  if (!searches.length) return new Set()
  const suppression = new Set<string>()
  const pages = await Promise.allSettled(
    searches.map((search) =>
      gqlQuery<OptOutKeysResult>(
        OPT_OUT_KEYS_QUERY,
        { search, pagination: { limit: OPT_OUT_KEYS_PER_QUERY } },
        // network-only: this rides the message poll, where a cached page would
        // keep a just-arrived opt-out out of the count for the whole session.
        { requestPolicy: `network-only` },
      ),
    ),
  )
  for (const page of pages) {
    if (page.status === `rejected`) {
      logError(`Error fetching opt-outs for unread counts`, page.reason)
      continue
    }
    for (const hit of page.value.data?.optOuts?.hits ?? []) {
      if (!hit.locationId || !hit.key) continue
      suppression.add(optOutSuppressionKey(hit.locationId, hit.key))
    }
  }
  return suppression
}
